// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — client DI types + transport seam + default factory (ABB-S3)
// ═══════════════════════════════════════════════════════════════════════════
//
// The structural DI surface for `AgentBridgeClient` (extracted from
// agentBridgeClient.ts to keep that file under the 500-line rule; barrel re-export
// keeps import paths stable). EVERY editor coupling (dispatch / getState /
// handlers / revision getters) arrives via these structural types — the module
// imports NO editorStore (ПРИОРИТЕТ #2: no editorStore↔agentBridge cycle).
//
// ALSO houses the TEST-ONLY transport-injection seam (`window.__agentBridgeTestTransport`)
// + the default socket factory, mirroring the existing `__edit_dwg_scene_force_install`
// window-seam pattern (a window property consulted at call time, inert in prod).
//
// LEAF module: imports ONLY the codec/reconnect types. No socket I/O, no
// editorStore, no node-companion.
// ═══════════════════════════════════════════════════════════════════════════

import type { ReconnectOptions } from "./reconnect";
import type { BridgeRole, InboundExportControlFrame } from "./wsFrameCodec";

/** The default page-JS connection status enum (a TYPED value S7's UI maps to i18n). */
export type AgentBridgeStatus =
  | "disconnected"
  | "connecting"
  | "connected-active"
  | "connected-observer"
  | "reconnecting"
  | "companion-not-reachable"
  | "token-rejected"
  | "protocol-version-mismatch";

// ── the minimal WebSocket surface the client uses (structural, DI-injectable) ─

/**
 * The minimal WebSocket-like surface the client drives. The browser `WebSocket`
 * satisfies it; the TEST-ONLY `window.__agentBridgeTestTransport` factory
 * returns a mock that satisfies it too. Declared structurally so the client
 * never hard-binds the global `WebSocket` (which is unavailable under jsdom WS
 * mocking) and so the dep-graph adds no DOM-lib edge beyond the ambient global.
 */
export interface BridgeSocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  set onopen(handler: (() => void) | null);
  set onclose(handler: ((event: { code: number; reason?: string }) => void) | null);
  set onerror(handler: (() => void) | null);
  set onmessage(handler: ((event: { data: unknown }) => void) | null);
}

/** A factory that opens a socket for the loopback URL (DI; default `new WebSocket`). */
export type BridgeSocketFactory = (url: string) => BridgeSocketLike;

/** The TEST-ONLY transport-injection seam shape (mirrors the §8 mock-companion). */
export interface AgentBridgeTestTransport {
  /** Behaviour mode the mock socket adopts on connect. */
  mode: "accept" | "refuse" | "reject-token";
  /** Open a mock socket for `url` (the client never inspects the URL beyond loopback). */
  open(url: string): BridgeSocketLike;
}

// ── DI: ALL editor coupling arrives here (typed structurally, never editorStore) ─

/**
 * The result an rpc handler returns: a terminal result body OR an error code.
 * Gate #4 / FIX#2 — the `{ok:false}` arm now carries an OPTIONAL `outcomeId`,
 * SYMMETRIC with the `{ok:true}` arm, so a failure outcome can surface a
 * provisional outcome handle through `serializeFailureTerminal` →
 * `buildRpcErrorFrame` (the companion already accepts it on `rpc-error`). No
 * producer sets it yet, but the field exists on both arms so it is never
 * silently dropped once the cached-error-replay path does.
 */
export type RpcHandlerOutcome =
  | { ok: true; batchHash?: string; result?: unknown; outcomeId?: string }
  | {
      ok: false;
      batchHash?: string;
      code: string;
      message: string;
      detail?: unknown;
      outcomeId?: string;
    };

/** The context an rpc handler receives (the frame + the current ownership epoch). */
export type RpcHandlerContext = {
  correlationId: string;
  sessionEpoch: number;
  deadlineBudgetMs?: number;
  payload: unknown;
};

/**
 * An injected rpc handler (S4 command.run, S6 state.read/export.dxf, MANIFEST
 * manifest.read). Returns a terminal outcome the client serializes into an
 * `rpc-result` / `rpc-error`. May be async (the client awaits it). A handler is
 * only CALLED for an rpc that survives the epoch-drop guard.
 */
export type RpcHandler = (
  ctx: RpcHandlerContext,
) => RpcHandlerOutcome | Promise<RpcHandlerOutcome>;

/**
 * The injected EXPORT FLOW-CONTROL handler (ABB-S6's `export.dxf` producer wires
 * it). The client PARSES + ROUTES each companion `export-pause`/`export-resume`/
 * `export-cancel` frame here so the producer can honor the companion's credit
 * cap / cancel (`exportBroker.ts` [H10]). Absent until S6 → the client still
 * PARSES the frame and routes it (a no-op), NEVER a silent drop — a dropped
 * pause/cancel would let the wired producer stream unboundedly. The frame is
 * routed only for a frame that survives the epoch-drop guard (same gate as rpc).
 */
export type ExportControlHandler = (frame: InboundExportControlFrame) => void;

/**
 * The DI surface. EVERY editor coupling is here — the client imports no
 * editorStore. `getHelloDocument` supplies the document identity for the hello
 * (read from the live store BY THE CALLER, never by this module). `handlers`
 * maps an rpc method to its injected handler (absent = known-but-unwired →
 * documented not-implemented reject). `onAbortInProgressTurn` fires on a drop so
 * S4 can roll back / clear its staged buffer. `onStatusChange` / `onRoleChange`
 * surface state to S7's UI. `socketFactory` defaults to `new WebSocket`.
 */
export interface AgentBridgeClientDeps {
  /** Read the live document identity for the hello (DI from the caller, store-free here). */
  getHelloDocument: () => { documentId: string | null; documentEpoch: number | null };
  /** Diagnostics for the hello (opaque, capped companion-side). */
  getClientInfo?: () => { clientInfo?: string; tabId?: string };
  /** rpc method → injected handler. Absent method = known-but-unwired (S4/S6/MANIFEST). */
  handlers?: Partial<Record<string, RpcHandler>>;
  /**
   * The ABB-S6 export FLOW-CONTROL seam: route a parsed `export-pause`/`-resume`/
   * `-cancel` to the export producer. Absent until S6 → the client still parses +
   * routes the frame (a no-op), never silently drops it.
   */
  onExportControl?: ExportControlHandler;
  /**
   * Fire so the in-progress turn aborts (no result delivered) on a socket DROP OR a
   * deliberate disconnect() mid-turn (the drop⇔disconnect SYMMETRY invariant — both
   * teardown paths must abort, else S4's in-flight latch / S5's staged audit leaks
   * into the next session). Fires AT MOST ONCE per teardown.
   */
  onAbortInProgressTurn?: () => void;
  /** Surface the status to S7's UI (a typed enum value, no UI here). */
  onStatusChange?: (status: AgentBridgeStatus) => void;
  /** Surface a role / epoch change to S5 (active vs observer / demotion gating). */
  onRoleChange?: (role: BridgeRole | null, sessionEpoch: number | null) => void;
  /**
   * ABB-S6.4 (1A) — HYDRATE-IN: surface the companion's CURRENT document/session belief
   * whenever it is adopted (a welcome / session-changed) or cleared (a disconnect). S7 wires
   * this to `documentEpochCache.hydrate(...)` / `.reset()` so the `state.read` path binds
   * each read to the correct snapshot belief — closing the project-OUT ⇔ hydrate-IN symmetry
   * the client's `refreshDocumentIdentityIfStale` flagged as the S6 gate-marker
   * (agentBridgeClient.ts:614-622, the project-OUT half). Carries the documentId/Epoch from
   * the adopted quad + the sessionEpoch; null members before pairing / after reset. Fires on
   * EVERY adoption (idempotent for an unchanged belief — the cache no-ops a same-belief
   * hydrate). Absent until S7 wires it → inert (no read consumer to notify pre-S7).
   */
  onDocumentChanged?: (belief: {
    documentId: string | null;
    documentEpoch: number | null;
    sessionEpoch: number | null;
  }) => void;
  /** Open a socket (DI; default `new WebSocket`). */
  socketFactory?: BridgeSocketFactory;
  /** Reconnect tuning (DI for deterministic tests). */
  reconnect?: ReconnectOptions;
  /** The connect URL (DI; defaults to the hardcoded loopback URL — tests may pin it). */
  wsUrl?: string;
}

/**
 * The default socket factory: prefers a TEST-ONLY `window.__agentBridgeTestTransport`
 * (set via a scenario `initScript` BEFORE mount) over `new WebSocket(...)`, gated
 * INERT in prod (mirrors `__edit_dwg_scene_force_install` / `editorRunAiCommandBatch`
 * — a window seam consulted at call time, tree-shake-safe, never auto-active). In a
 * real prod page the property is absent → the real `WebSocket` is used.
 */
export function defaultSocketFactory(url: string): BridgeSocketLike {
  if (typeof window !== "undefined") {
    const transport = (window as unknown as Record<string, unknown>)[
      "__agentBridgeTestTransport"
    ] as AgentBridgeTestTransport | undefined;
    if (transport && typeof transport.open === "function") {
      return transport.open(url);
    }
  }
  // Real prod path: the native WebSocket. Cast through the structural surface.
  return new WebSocket(url) as unknown as BridgeSocketLike;
}
